// Where to put a zone's number. Two ideas combined:
//  - the shape's centroid (its balance point by area), which looks centred on lopsided shapes
//  - room to the nearest edge, so the number sits comfortably inside the zone
// labelPoint() picks the point closest to the centroid that still has at least half of the
// most room any point in the shape has. Even shapes land in the middle; lopsided shapes
// lean toward their visual middle; L-shapes and curved bands (where the centroid can fall
// outside) get the nearest comfortable spot inside.

const pointInPolygon = (x, y, poly) => {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i, i += 1) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
};

const distToSegment = (px, py, [ax, ay], [bx, by]) => {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 ? ((px - ax) * dx + (py - ay) * dy) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  const cx = ax + t * dx;
  const cy = ay + t * dy;
  return Math.hypot(px - cx, py - cy);
};

// Distance to the nearest edge, negative when outside
const edgeDistance = (x, y, poly) => {
  let min = Infinity;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i, i += 1) {
    min = Math.min(min, distToSegment(x, y, poly[j], poly[i]));
  }
  return pointInPolygon(x, y, poly) ? min : -min;
};

// Centroid of the polygon's area (shoelace formula)
export const areaCentroid = (poly) => {
  let a = 0; let cx = 0; let cy = 0;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i, i += 1) {
    const [x0, y0] = poly[j];
    const [x1, y1] = poly[i];
    const cross = x0 * y1 - x1 * y0;
    a += cross;
    cx += (x0 + x1) * cross;
    cy += (y0 + y1) * cross;
  }
  if (Math.abs(a) < 1e-9) return null;
  return { x: cx / (3 * a), y: cy / (3 * a) };
};

// poly: [[x, y], ...] outline points. Returns { x, y } or null.
export const poleOfInaccessibility = (poly) => {
  if (!poly || poly.length < 3) return null;
  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
  poly.forEach(([x, y]) => {
    minX = Math.min(minX, x); minY = Math.min(minY, y);
    maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
  });
  let best = { x: (minX + maxX) / 2, y: (minY + maxY) / 2, d: -Infinity };
  best.d = edgeDistance(best.x, best.y, poly);

  // Coarse grid over the whole shape, then zoom in around the best point
  let step = Math.max(maxX - minX, maxY - minY) / 24;
  let cx0 = minX; let cy0 = minY; let cx1 = maxX; let cy1 = maxY;
  for (let round = 0; round < 4 && step > 0.25; round += 1) {
    for (let x = cx0; x <= cx1; x += step) {
      for (let y = cy0; y <= cy1; y += step) {
        const d = edgeDistance(x, y, poly);
        if (d > best.d) best = { x, y, d };
      }
    }
    cx0 = best.x - step; cx1 = best.x + step;
    cy0 = best.y - step; cy1 = best.y + step;
    step /= 4;
  }
  return best.d > 0 ? { x: best.x, y: best.y } : null;
};

// Sample a rendered SVG shape's outline (path, polygon or rect) into points
export const outlineOf = (el) => {
  if (!el || typeof el.getTotalLength !== 'function') return null;
  let total;
  try {
    total = el.getTotalLength();
  } catch (e) {
    return null;
  }
  if (!total) return null;
  const n = Math.max(48, Math.min(320, Math.round(total / 4)));
  const pts = [];
  for (let i = 0; i < n; i += 1) {
    const p = el.getPointAtLength((total * i) / n);
    pts.push([p.x, p.y]);
  }
  return pts;
};

// Balanced placement: nearest point to the area centroid with at least `share` of the
// deepest point's room to the edges (see the note at the top of this file).
export const labelPoint = (poly, share = 0.5) => {
  const pole = poleOfInaccessibility(poly);
  if (!pole) return null;
  const centroid = areaCentroid(poly);
  if (!centroid) return pole;
  const maxRoom = edgeDistance(pole.x, pole.y, poly);
  const need = maxRoom * share;
  // The centroid itself, when it has enough room
  if (edgeDistance(centroid.x, centroid.y, poly) >= need) return centroid;

  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
  poly.forEach(([x, y]) => {
    minX = Math.min(minX, x); minY = Math.min(minY, y);
    maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
  });
  const toCentroid = (x, y) => Math.hypot(x - centroid.x, y - centroid.y);
  let best = { x: pole.x, y: pole.y, c: toCentroid(pole.x, pole.y) };

  // Grid search, then zoom in around the best spot
  let step = Math.max(maxX - minX, maxY - minY) / 32;
  let x0 = minX; let y0 = minY; let x1 = maxX; let y1 = maxY;
  for (let round = 0; round < 3 && step > 0.25; round += 1) {
    for (let x = x0; x <= x1; x += step) {
      for (let y = y0; y <= y1; y += step) {
        const c = toCentroid(x, y);
        if (c < best.c && edgeDistance(x, y, poly) >= need) best = { x, y, c };
      }
    }
    x0 = best.x - step; x1 = best.x + step;
    y0 = best.y - step; y1 = best.y + step;
    step /= 4;
  }
  return { x: best.x, y: best.y };
};
